import React, { useRef, useState } from 'react'
import {
  Box,
  TextField,
  IconButton,
  Button,
  Chip,
  Typography,
  Tooltip,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { BrandPaletteEntry } from '../../types/gotypes'

interface BrandPaletteEditorProps {
  palette: Record<string, BrandPaletteEntry>
  onChange: (palette: Record<string, BrandPaletteEntry>) => void
}

// Normalize a typed/pasted hex value: accepts "4E79A7", "#4E79A7" and the
// 3-digit shorthand. Returns a canonical "#rrggbb" string, or null if the
// input is not (yet) a complete hex colour.
export function normalizeHex(input: string): string | null {
  const raw = input.trim().replace(/^#/, '')
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw.toLowerCase()}`
  if (/^[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw.split('').map((c) => c + c).join('').toLowerCase()}`
  }
  return null
}

interface HexColorFieldProps {
  value: string
  onCommit: (hex: string) => void
  label?: string
}

// Hex-first colour input (#973 item 1): a text field a brand hex pastes
// straight into, kept in sync with the adjacent native colour picker. Local
// draft state lets the user type partial values; a valid hex commits
// immediately. Render-phase ref guard (not an effect) resyncs the draft when
// the committed value changes from outside (e.g. the picker).
const HexColorField: React.FC<HexColorFieldProps> = ({ value, onCommit, label = 'Hex' }) => {
  const [draft, setDraft] = useState(value)
  const lastValueRef = useRef(value)
  if (lastValueRef.current !== value) {
    lastValueRef.current = value
    if (normalizeHex(draft) !== value) setDraft(value)
  }

  const handleChange = (raw: string) => {
    setDraft(raw)
    const hex = normalizeHex(raw)
    if (hex && hex !== value) onCommit(hex)
  }

  return (
    <TextField
      size="small"
      label={label}
      value={draft}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={() => {
        // Snap an abandoned partial edit back to the committed colour.
        if (normalizeHex(draft) === null) setDraft(value)
      }}
      placeholder="#4e79a7"
      error={draft.trim() !== '' && normalizeHex(draft) === null}
      slotProps={{ htmlInput: { spellCheck: false, style: { fontFamily: 'monospace' } } }}
      sx={{ width: 110 }}
    />
  )
}

const colorInputStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  background: 'none',
  flexShrink: 0,
}

const BrandPaletteEditor: React.FC<BrandPaletteEditorProps> = ({
  palette,
  onChange,
}) => {
  const [newBrandName, setNewBrandName] = useState('')
  const [newBrandColor, setNewBrandColor] = useState('#4e79a7')
  const [newBrandAliases, setNewBrandAliases] = useState('')

  // In-place row editing (#973 item 2)
  const [editingBrand, setEditingBrand] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState('')
  const [editAliases, setEditAliases] = useState('')

  const parseAliases = (raw: string) =>
    raw.split(',').map((a) => a.trim()).filter(Boolean)

  const handleAddBrand = () => {
    const brandName = newBrandName.trim()
    if (!brandName) return
    const aliases = parseAliases(newBrandAliases)
    onChange({
      ...palette,
      [brandName]: {
        color: newBrandColor,
        ...(aliases.length > 0 ? { aliases } : {}),
      },
    })
    setNewBrandName('')
    setNewBrandColor('#4e79a7')
    setNewBrandAliases('')
  }

  const handleDeleteBrand = (brandName: string) => {
    const next = { ...palette }
    delete next[brandName]
    onChange(next)
    if (editingBrand === brandName) setEditingBrand(null)
  }

  const handleUpdateBrandColor = (brandName: string, color: string) => {
    onChange({
      ...palette,
      [brandName]: { ...palette[brandName], color },
    })
  }

  const handleStartEdit = (brandName: string) => {
    const entry = palette[brandName]
    if (!entry) return
    setEditingBrand(brandName)
    setEditName(brandName)
    setEditColor(entry.color)
    setEditAliases((entry.aliases || []).join(', '))
  }

  const editNameTrimmed = editName.trim()
  const editNameCollides =
    editingBrand !== null &&
    editNameTrimmed !== editingBrand &&
    editNameTrimmed in palette

  const handleConfirmEdit = () => {
    if (!editingBrand || !editNameTrimmed || editNameCollides) return
    const aliases = parseAliases(editAliases)
    const entry: BrandPaletteEntry = {
      ...palette[editingBrand],
      color: editColor,
      ...(aliases.length > 0 ? { aliases } : { aliases: undefined }),
    }
    if (aliases.length === 0) delete entry.aliases
    // Rebuild preserving row order so a rename doesn't jump the row around.
    const next: Record<string, BrandPaletteEntry> = {}
    for (const [name, value] of Object.entries(palette)) {
      if (name === editingBrand) {
        next[editNameTrimmed] = entry
      } else {
        next[name] = value
      }
    }
    onChange(next)
    setEditingBrand(null)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {Object.entries(palette).map(([brandName, entry]) =>
        editingBrand === brandName ? (
          <Box key={brandName} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <input
              type="color"
              aria-label={`Pick color for ${brandName}`}
              value={editColor}
              onChange={(e) => setEditColor(e.target.value)}
              style={colorInputStyle}
            />
            <HexColorField value={editColor} onCommit={setEditColor} />
            <TextField
              size="small"
              label="Brand name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmEdit() }}
              error={editNameCollides}
              helperText={editNameCollides ? 'A brand with this name already exists' : undefined}
              autoFocus
              sx={{ flex: 1, minWidth: 120 }}
            />
            {/* #1240 A4: the row commit is a labelled button, not a bare tick —
                Matt missed the tick and thought the alias had not taken.
                Helper text says what aliases are and that the theme still
                needs saving. */}
            <TextField
              size="small"
              label="Aliases — other names for this series"
              placeholder="e.g. NET Any Sky, Sky Sport"
              helperText="Comma-separated. Click Apply, then save the theme."
              value={editAliases}
              onChange={(e) => setEditAliases(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmEdit() }}
              sx={{ flex: 1, minWidth: 200 }}
            />
            <Button
              size="small"
              variant="contained"
              color="info"
              startIcon={<CheckIcon fontSize="small" />}
              aria-label={`Save changes to ${brandName}`}
              onClick={handleConfirmEdit}
              disabled={!editNameTrimmed || editNameCollides}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              Apply
            </Button>
            <Tooltip title="Cancel">
              <IconButton
                size="small"
                aria-label={`Cancel editing ${brandName}`}
                onClick={() => setEditingBrand(null)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box key={brandName} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="color"
              aria-label={`Pick color for ${brandName}`}
              value={entry.color}
              onChange={(e) => handleUpdateBrandColor(brandName, e.target.value)}
              style={colorInputStyle}
            />
            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 80 }}>
              {brandName}
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {entry.aliases?.map((alias) => (
                <Chip key={alias} label={alias} size="small" variant="outlined" />
              ))}
            </Box>
            <Tooltip title="Edit brand">
              <IconButton
                size="small"
                aria-label={`Edit ${brandName}`}
                onClick={() => handleStartEdit(brandName)}
                sx={{ '&:hover': { color: 'info.main' } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove brand">
              <IconButton
                size="small"
                aria-label={`Remove ${brandName}`}
                onClick={() => handleDeleteBrand(brandName)}
                sx={{ '&:hover': { color: 'error.main' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="color"
          aria-label="Pick color for new brand"
          value={newBrandColor}
          onChange={(e) => setNewBrandColor(e.target.value)}
          style={colorInputStyle}
        />
        <HexColorField value={newBrandColor} onCommit={setNewBrandColor} />
        <TextField
          size="small"
          label="Brand name"
          value={newBrandName}
          onChange={(e) => setNewBrandName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddBrand() }}
          sx={{ flex: 1, minWidth: 120 }}
        />
        <TextField
          size="small"
          label="Aliases — other names for this series"
          placeholder="e.g. NET Any Sky"
          helperText="Comma-separated. Matched when colouring."
          value={newBrandAliases}
          onChange={(e) => setNewBrandAliases(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddBrand() }}
          sx={{ flex: 1, minWidth: 140 }}
        />
        <Tooltip title="Add brand">
          <span>
            <IconButton
              size="small"
              onClick={handleAddBrand}
              disabled={!newBrandName.trim()}
              color="info"
              aria-label="Add brand"
            >
              <AddIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default BrandPaletteEditor
